// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The questions a route answers under its own content (M12-30).
//
// WHY A SECOND COMPONENT RATHER THAN A FLAG ON `CopySections`
// ----------------------------------------------------------
// The same reason `CopySections` is not `CopyPage` with a flag, written down in that file: a shared
// component would have to grow a parameter saying which of the two it was being, and the parameter
// would be the bug. A question and its answer are a definition list; a section is a heading and
// paragraphs. They are different markup and they travel separately.
//
// WHAT THIS IS FOR
// ----------------
// `faq` was renderable in exactly one place — `CopyPage`, the six prose documents — while the
// prerenderer's `FAQPage` markup is written for **any** page that carries `faq`. So the field could
// not be used on a route whose body is a roster, a price table or an editor without writing marked-up
// text a visitor cannot read, which is the failure report 01 §C5 records in the sibling product and
// which `prerender.ts` forbids in as many words. A guard held the field to one page instead; this
// component is the other way to satisfy the same rule, and `test/seo/pageCopy.test.ts` now asserts
// the pairing rather than the single page.
//
// The heading is the literal `CopyPage` and the prerenderer both write. Three copies of two words,
// and `test/seo/pageCopy.test.ts` holds them together — a heading that differed between the markup
// a crawler reads and the page a visitor reads would be the same drift in miniature.

import type { PageCopy } from '../../seo/pageCopy'

/**
 * @param questions What to draw. Absent or empty renders nothing: a route may carry no questions,
 *   and an empty "Common questions" heading is worse than no section.
 */
export function CopyFaq({
  questions,
}: {
  // `| undefined` explicitly, because `exactOptionalPropertyTypes` is on and every call site passes
  // `PAGE_COPY[route]?.faq` — a lookup that may be absent, which is the whole point of the prop.
  readonly questions?: PageCopy['faq'] | undefined
}): React.JSX.Element | null {
  if (questions === undefined || questions.length === 0) return null
  return (
    <div className="page copy copy--below">
      <section className="copy__section">
        <h2>Common questions</h2>
        <dl className="copy__faq">
          {questions.map((entry) => (
            <div key={entry.question}>
              <dt>{entry.question}</dt>
              <dd>{entry.answer}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  )
}
